import React from 'react';
import { motion } from 'framer-motion';
import { Star, ShoppingCart, Heart, ChevronRight, Cpu } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CartSidebar } from '../components/CartSidebar';
import { CartItem, Page } from '../App';
const electronicsProducts = [{
  id: 'e1',
  name: 'Redmi Note 15 Pro',
  price: 500000,
  image: 'https://cdn.magicpatterns.com/uploads/8cBVP6dQJouJukeTEWCVEZ/Redmi_Note_15_Pro.jpg',
  rating: 4.9,
  badge: 'Featured',
  weight: 0.2,
  sku: 'ELEC-RN15P-001',
  description: 'Flagship performance meets elegant design. Stunning camera system and powerful processor.'
}, {
  id: 'e2',
  name: 'iPhone 15',
  price: 850000,
  image: 'https://images.unsplash.com/photo-1695048133142-1a20484d2569?auto=format&fit=crop&q=80&w=800',
  rating: 4.9,
  badge: 'Premium',
  weight: 0.17,
  sku: 'ELEC-IP15-002',
  description: 'Apple iPhone 15, 128GB, Dynamic Island, 48MP camera'
}, {
  id: 'e3',
  name: 'Samsung Galaxy A55',
  price: 320000,
  image: 'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?auto=format&fit=crop&q=80&w=800',
  rating: 4.7,
  weight: 0.21,
  sku: 'ELEC-SGA55-003',
  description: 'Samsung Galaxy A55 5G, 256GB, 50MP triple camera'
}, {
  id: 'e4',
  name: 'Wireless Earbuds Pro',
  price: 45000,
  originalPrice: 60000,
  image: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?auto=format&fit=crop&q=80&w=800',
  rating: 4.5,
  badge: 'Sale',
  weight: 0.06,
  sku: 'ELEC-WEP-004',
  description: 'Active noise cancellation, 30hr battery, wireless charging'
}, {
  id: 'e5',
  name: 'Smart TV 43"',
  price: 145000,
  image: 'https://images.unsplash.com/photo-1593784991095-a20506948430?auto=format&fit=crop&q=80&w=800',
  rating: 4.8,
  weight: 8.0,
  sku: 'ELEC-STV43-005',
  description: '4K Smart TV, Android OS, built-in WiFi & Bluetooth'
}, {
  id: 'e6',
  name: 'Laptop 15.6" Core i5',
  price: 480000,
  image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&q=80&w=800',
  rating: 4.7,
  weight: 1.8,
  sku: 'ELEC-LPT-006',
  description: 'Intel Core i5, 16GB RAM, 512GB SSD, Windows 11'
}, {
  id: 'e7',
  name: 'Portable Power Bank 20000mAh',
  price: 18000,
  image: 'https://images.unsplash.com/photo-1609091839311-d5365f9ff1c5?auto=format&fit=crop&q=80&w=800',
  rating: 4.6,
  weight: 0.45,
  sku: 'ELEC-PBK-007',
  description: '20000mAh fast charging power bank, dual USB-C output'
}, {
  id: 'e8',
  name: 'Smart Watch Series 8',
  price: 95000,
  originalPrice: 120000,
  image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&q=80&w=800',
  rating: 4.8,
  badge: 'Popular',
  weight: 0.05,
  sku: 'ELEC-SWS8-008',
  description: 'Health monitoring, GPS, 18hr battery, water resistant'
}];
interface PageProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
  cartItems: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
export function ElectronicsPage({
  onAddToCart,
  onNavigate,
  cartItems,
  cartCount,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onUpdateQuantity,
  onRemoveItem
}: PageProps) {
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(amount);
  const featured = electronicsProducts[0];
  return <div className="bg-[#0F0F0F] min-h-screen text-white" data-id="element-423">
      <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-424" />

      <div className="pt-28 pb-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-425">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-id="element-426">
          <button onClick={() => onNavigate('home')} className="hover:text-[#D4AF37] transition-colors" data-id="element-427">
            Home
          </button>
          <ChevronRight className="w-4 h-4" data-id="element-428" />
          <span className="text-blue-400" data-id="element-429">Electronics</span>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-4 mb-4" data-id="element-430">
          <div className="p-3 bg-blue-500/10 rounded-xl" data-id="element-431">
            <Cpu className="w-7 h-7 text-blue-400" data-id="element-432" />
          </div>
          <div data-id="element-433">
            <h1 className="text-4xl md:text-5xl font-bold font-serif text-white" data-id="element-434">
              Electronics
            </h1>
            <p className="text-gray-400 mt-1" data-id="element-435">
              Latest gadgets, phones & tech delivered nationwide
            </p>
          </div>
        </motion.div>
      </div>

      {/* Featured Product Hero */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16" data-id="element-436">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="glass-panel rounded-2xl overflow-hidden border border-[#D4AF37]/20" data-id="element-437">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" data-id="element-438">
            <div className="relative h-72 md:h-auto bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-8" data-id="element-439">
              <img src={featured.image} alt={featured.name} className="h-full max-h-64 object-contain drop-shadow-2xl" data-id="element-440" />
              <span className="absolute top-4 left-4 bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 rounded-full" data-id="element-441">
                ⭐ Featured
              </span>
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-442">
              <p className="text-blue-400 text-sm uppercase tracking-widest font-medium mb-2" data-id="element-443">
                SKU: {featured.sku}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-3" data-id="element-444">
                {featured.name}
              </h2>
              <p className="text-gray-400 mb-4" data-id="element-445">{featured.description}</p>
              <div className="flex items-center gap-2 mb-6" data-id="element-446">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" data-id="element-447" />)}
                <span className="text-gray-400 text-sm" data-id="element-448">
                  ({featured.rating}/5.0)
                </span>
              </div>
              <div className="flex items-center gap-4 mb-6" data-id="element-449">
                <span className="text-3xl font-bold text-[#D4AF37]" data-id="element-450">
                  {formatCurrency(featured.price)}
                </span>
                <span className="text-gray-500 text-sm" data-id="element-451">
                  · {featured.weight}kg
                </span>
              </div>
              <button onClick={() => onAddToCart(featured)} className="flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#c4a030] transition-all w-full md:w-auto" data-id="element-452">
                <ShoppingCart className="w-5 h-5" data-id="element-453" />
                Add to Cart
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* All Products */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24" data-id="element-454">
        <h3 className="text-2xl font-bold font-serif text-white mb-8" data-id="element-455">
          All Electronics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-456">
          {electronicsProducts.map((product, index) => <motion.div key={product.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.07
        }} className="glass-card rounded-xl overflow-hidden group flex flex-col" data-id="element-457">
              <div className="relative h-56 overflow-hidden bg-gray-900" data-id="element-458">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-459" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" data-id="element-460" />
                {product.badge && <span className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded" data-id="element-461">
                    {product.badge}
                  </span>}
                <button className="absolute top-3 right-3 p-2 bg-white/10 backdrop-blur-sm text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:text-black" data-id="element-462">
                  <Heart className="w-4 h-4" data-id="element-463" />
                </button>
              </div>
              <div className="p-4 flex-1 flex flex-col" data-id="element-464">
                <p className="text-xs text-gray-500 mb-1" data-id="element-465">SKU: {product.sku}</p>
                <h3 className="text-white font-medium mb-1 line-clamp-1" data-id="element-466">
                  {product.name}
                </h3>
                <p className="text-gray-500 text-xs mb-3 line-clamp-2" data-id="element-467">
                  {product.description}
                </p>
                <div className="flex items-center gap-1 mb-3" data-id="element-468">
                  <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" data-id="element-469" />
                  <span className="text-xs text-gray-400" data-id="element-470">
                    {product.rating}
                  </span>
                  <span className="text-xs text-gray-600 ml-1" data-id="element-471">
                    · {product.weight}kg
                  </span>
                </div>
                <div className="mt-auto flex items-center justify-between" data-id="element-472">
                  <div data-id="element-473">
                    <span className="text-lg font-bold text-white" data-id="element-474">
                      {formatCurrency(product.price)}
                    </span>
                    {product.originalPrice && <p className="text-xs text-gray-500 line-through" data-id="element-475">
                        {formatCurrency(product.originalPrice)}
                      </p>}
                  </div>
                  <button onClick={() => onAddToCart(product)} className="p-3 bg-white/10 hover:bg-blue-500 text-white rounded-lg transition-colors" data-id="element-476">
                    <ShoppingCart className="w-4 h-4" data-id="element-477" />
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      <Footer onNavigate={onNavigate} data-id="element-478" />
      <CartSidebar isOpen={isCartOpen} onClose={onCloseCart} items={cartItems} onUpdateQuantity={onUpdateQuantity} onRemoveItem={onRemoveItem} onNavigate={onNavigate} data-id="element-479" />
    </div>;
}